/**
 * Collection games run in a cross-origin iframe and talk to the parent with
 * postMessage. Only that iframe's exact origin may ask us to sign or navigate,
 * and a navigation target has to stay on this site over https.
 */

const BTC_WAR_SLUGS = [
	'btc-machines',
	'btc-land',
	'btc-virus',
	'btc-artifacts',
	'btc-survivors',
	'btc-mutant-survivors',
	'btc-mutant-machine'
]

export type GameEmbed = {
	src: string
	origins: readonly string[]
	layout: 'default' | 'btc-war'
	title: string
}

function originsFor(src: string): readonly string[] {
	const url = new URL(src)
	const exact = url.origin
	if (url.hostname.toLowerCase().startsWith('www.')) return [exact]
	return [exact, `${url.protocol}//www.${url.hostname}`]
}

function embed(src: string, title: string, layout: GameEmbed['layout']): GameEmbed {
	return { src, origins: originsFor(src), layout, title }
}

export function gameEmbed(slug: string): GameEmbed | null {
	if (slug === 'coom-battles')
		return embed('https://championstcg.com/app/', 'Coom Battles game', 'default')
	if (slug === 'based-apes')
		return embed('https://basedapes.com/bananabounce', 'Banana Bounce game', 'default')
	if (BTC_WAR_SLUGS.includes(slug))
		return embed('https://playbtcwar.com/', 'Play BTC War game', 'btc-war')
	return null
}

export function isAllowedGameOrigin(slug: string, origin: string): boolean {
	const frame = gameEmbed(slug)
	return !!frame && frame.origins.includes(origin)
}

function siteKey(hostname: string): string {
	const host = hostname.toLowerCase().replace(/\.$/, '')
	if (host === 'ordinalswallet.com' || host.endsWith('.ordinalswallet.com'))
		return 'ordinalswallet.com'
	return host
}

/** https URL on the same site as the page, or null. javascript: and off-site targets are refused. */
export function sameSiteHttpsRedirect(raw: unknown, pageHref: string): string | null {
	if (typeof raw !== 'string') return null
	const trimmed = raw.trim()
	if (!trimmed || trimmed.length > 2048) return null
	if (/[\u0000-\u001f\u007f]/.test(trimmed)) return null
	if (/^(javascript|data|vbscript|blob):/i.test(trimmed)) return null

	let page: URL
	let url: URL
	try {
		page = new URL(pageHref)
		url = new URL(trimmed, pageHref)
	} catch {
		return null
	}
	if (url.protocol !== 'https:') return null
	if (url.username || url.password) return null
	if (siteKey(url.hostname) !== siteKey(page.hostname)) return null
	return url.href
}
